import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, Output, EventEmitter, Injector } from '@angular/core';
import { SCFPrivileges, ScfV2Service } from '../scf-v2.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { SCFMaster } from '../scf';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ScfV2MainS1Component } from './scf-v2-main-s1/scf-v2-main-s1.component';
import { ScfV2MainS2Component } from './scf-v2-main-s2/scf-v2-main-s2.component';
import { ScfV2MainS3Component } from './scf-v2-main-s3/scf-v2-main-s3.component';
import { ScfV2MainS4Component } from './scf-v2-main-s4/scf-v2-main-s4.component';
import { ScfV2MainS5Component } from './scf-v2-main-s5/scf-v2-main-s5.component';
import { FormViewState } from 'src/app/common/models/form-view-state';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
@Component({
  selector: 'scf-v2-main',
  templateUrl: './scf-v2-main.component.html',
  styleUrls: ['./scf-v2-main.component.scss']
})
export class ScfV2MainComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input() scfMaster?: SCFMaster | null;
  @Input() privileges?: SCFPrivileges;
  @Input() status?: FormViewState;
  @Input() disabled?: boolean;
  @Input() showAmendment?: boolean;

  @Output() setStatus = new EventEmitter<FormViewState>();
  @Output() saved = new EventEmitter<SCFMaster>();
  // //@Output() loading = new EventEmitter<boolean>();

  @ViewChild(ScfV2MainS1Component) section1!: ScfV2MainS1Component;
  @ViewChild(ScfV2MainS2Component) section2?: ScfV2MainS2Component;
  @ViewChild(ScfV2MainS3Component) section3?: ScfV2MainS3Component;
  @ViewChild(ScfV2MainS4Component) section4?: ScfV2MainS4Component;
  @ViewChild(ScfV2MainS5Component) section5?: ScfV2MainS5Component;

  loading1: boolean = false;
  loading3: boolean = false;
  loading4: boolean = false;
  loading5: boolean = false;

  /** Guard so a second auto-advance can't start while one is in flight. */
  advancing = false;


  /**
   * Tester-only "Auto-advance" options, in lifecycle order. Each carries the target
   * FormStatusEnum and its logical advance rank (the raw enum ints aren't monotonic). "Active"
   * covers both Active and Active/Hold Point (Holdpoints), which share a rank — see advanceRank.
   */
  readonly advanceTargets: { label: string; status: FormStatusEnum; rank: number }[] = [
    { label: 'Active', status: FormStatusEnum.Active, rank: 2 },
    { label: 'Awaiting Inspection', status: FormStatusEnum.AwaitingInspection, rank: 3 },
    { label: 'Work Completed', status: FormStatusEnum.WorkCompleted, rank: 4 },
  ];

  constructor(
    private scfService: ScfV2Service,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  /**
   * Whether the "Tester Tools" master toggle should be shown: only on non-production hosts AND
   * for System Administrators. The toggle itself is always visible to that audience (so they can
   * turn the tooling on); the individual tools additionally require the toggle to be ON.
   */
  get showTesterToggle(): boolean {
    return !this.environmentService.isProduction() && this.isSA();
  }

  /** Two-way accessor for the shared Tester Tools master switch (bound to the toggle). */
  get testerToolsEnabled(): boolean {
    return this.testerToolsService.enabled;
  }
  setTesterToolsEnabled(value: boolean): void {
    this.testerToolsService.enabled = value;
  }

  /**
   * Whether the tester-only "Auto-advance" tooling should be shown. Gated defensively: only on
   * non-production hosts, for System Administrators, with the Tester Tools toggle ON, and only
   * once the form has been submitted (Work Approvals or later) — there is nothing to auto-advance
   * before then. The server-side TestAdvance endpoint is the real guard.
   */
  get showAutoAdvance(): boolean {
    return (
      this.showTesterToggle &&
      this.testerToolsService.enabled &&
      this.advanceRank(this.scfMaster?.status) >= 0
    );
  }

  /**
   * Logical advancement rank for the SCF lifecycle, mirroring the backend ScfAdvanceRank so the
   * dropdown options grey out once the form is at/past them. Active and Hold Point share rank 2.
   * Returns -1 for statuses off the linear advance path (Draft, Closed, amendment states).
   */
  advanceRank(status?: number): number {
    switch (status) {
      case FormStatusEnum.WorkApprovals: return 0;
      case FormStatusEnum.PendingActivation: return 1;
      case FormStatusEnum.Active:
      case FormStatusEnum.HoldPoint: return 2;
      case FormStatusEnum.AwaitingInspection: return 3;
      case FormStatusEnum.WorkCompleted: return 4;
      case FormStatusEnum.Closed: return 5;
      default: return -1;
    }
  }

  /** An auto-advance option is available only while the form is below that target's rank. */
  canAdvanceTo(target: { rank: number }): boolean {
    return this.advanceRank(this.scfMaster?.status) < target.rank;
  }

  /**
   * Tester tooling (non-production): asks the backend to apply every signature needed to advance
   * the form to the chosen status, in one request. The SignalR refresh that follows reloads the
   * form for all open clients.
   */
  autoAdvance(target: { label: string; status: FormStatusEnum; rank: number }): void {
    if (!this.scfMaster?.id || !this.showAutoAdvance || !this.canAdvanceTo(target) || this.advancing) return;
    this.advancing = true;
    this.scfService.testAdvance(this.scfMaster.id, target.status).toPromise().then(
      (data) => {
        this.advancing = false;
        if (data) this.changed(data);
      },
      (error) => {
        this.advancing = false;
        console.log(error);
        this.alert.defaultError();
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  ngOnInit(): void {
  }

  refresh() {
    this.section2?.refresh();
    this.section3?.refresh();
    this.section4?.refresh();
    this.section5?.refresh();
  }

  changed(e: SCFMaster | undefined | null = this.scfMaster) {
    this.scfMaster = e;
    this.refresh();
  }

  setStatusEvent(status: FormViewState) {
    this.setStatus.emit(status);
  }

  savedEvent(e: SCFMaster) {
    this.saved.emit(e);
  }

  // Side-effect-free validity for the button's [valid] binding. Must NOT call
  // section1.formValid() (which mutates control state) during change detection.
  formValid() {
    return this.section1?.isValid();
  }

  setChecksFromWorkRequired(e: any) {
    this.section5?.setChecksFromWorkRequired(e);
  }

  setAutoRestriction(e: number[]) {
    this.section2?.locationsAdded(e);
  }

  addCustomRestriction(text: string) {
    this.section2?.addCustomRestriction(text);
  }

  /**
   * Tester Reset routine, orchestrated across sections in order so each save
   * settles before the next step:
   *  1. clear the Section 1 fields and Section 2 restrictions (into the shared
   *     scfMaster),
   *  2. save the now-cleared master once and AWAIT it — so the SignalR refresh
   *     that follows reloads the cleared state instead of the old data,
   *  3. clear the Operational Approval checks via SaveTasks (its refresh also
   *     then reloads the cleared, saved master).
   */
  async resetFields() {
    this.section1?.clearFieldsForReset();
    this.section2?.clearRestrictions();
    await this.section5?.saveSCFMaster();
    this.section5?.clearOperationalApprovals();
  }
}
